import 'twin.macro';
import React, { useRef, useState } from 'react';
import { useFeatureFlagEnabled } from 'utils/useFeatureFlagEnabled';
import {
  AIGenerationParams,
  AIGenerationProvider,
} from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { useAIErrorHandler } from 'components/AI/hooks/useAIErrorHandler';
import { SelectionToolbar } from 'components/Editor/AIAssist/components/SelectionToolbar';
import { PromptButton } from 'components/Editor/AIAssist/components/AIPromptButton/PromptButton';
import { PromptPanel } from 'components/Editor/AIAssist/components/AIPromptButton/PromptPanel';
import { FroalaEditorInstance } from 'components/Editor/froalaTypes';
import tw, { css } from 'twin.macro';
import { AIGeneratedDisplayProps } from 'components/AI/AIGeneratedDisplay';

/**
 * AI Panel Type
 *
 * Only one panel can be active at a time per editor instance:
 * - 'customPrompt': User is creating content from scratch with a custom AI prompt
 * - 'selectionResult': AI generated result from improving selected text
 * - null: No panel is shown
 */
export type AIPanelType = 'customPrompt' | 'selectionResult' | null;

export interface AIHook {
  data: string;
  error: string | null;
  isLoading: boolean;
  startGeneration: (params: AIGenerationParams) => void;
  stop: () => void;
  reset: () => void;
  promptPlaceholder: AIGeneratedDisplayProps['placeholder'];
  promptDescription: AIGeneratedDisplayProps['promptDescription'];
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  onCancel?: () => void;
  children: React.ReactElement;
  inline?: boolean;
  aiHook?: AIHook;
  trackingContext?: string;
  onPdfAddAttachment?: (file: File) => Promise<void>;
  onPauseForSession?: () => void;
  onPauseUntilReactivate?: () => void;
  hideSelectionToolbar?: boolean;
}

function EditorAIWrapper({
  children,
  onChange,
  inline,
  aiHook,
  trackingContext,
  onPdfAddAttachment,
  onPauseForSession,
  onPauseUntilReactivate,
  hideSelectionToolbar = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const froalaInstanceRef = useRef<FroalaEditorInstance | null>(null);
  const [panelState, setPanelState] = useState<AIPanelType>(null);

  useAIErrorHandler(aiHook?.error || null);

  if (!aiHook) {
    return <div ref={containerRef}>{children}</div>;
  }

  const childrenWithFocusHandlers = React.cloneElement(children, {
    froalaInstanceRef,
  });

  return (
    <AIGenerationProvider
      aiHook={aiHook}
      trackingContext={trackingContext}
      onPauseForSession={onPauseForSession}
      onPauseUntilReactivate={onPauseUntilReactivate}
    >
      <div tw="relative">
        <div
          css={[
            css`
              .fr-second-toolbar {
                // this is pure bricolage to have the AI button within this unused toolbar space
                // with proper editor body scrollbar and padding
                height: 48px;
              }
            `,
            tw`relative`,
          ]}
          ref={containerRef}
        >
          {childrenWithFocusHandlers}
          <PromptButton
            visible={!inline}
            panelState={panelState}
            setPanelState={setPanelState}
          />
        </div>

        <SelectionToolbar
          visible={!inline && !hideSelectionToolbar}
          containerRef={containerRef}
          froalaInstanceRef={froalaInstanceRef}
          onChange={onChange}
          onPdfAddAttachment={onPdfAddAttachment}
        />

        <PromptPanel
          onPdfAddAttachment={onPdfAddAttachment}
          containerRef={containerRef}
          froalaInstanceRef={froalaInstanceRef}
          visible={!inline}
          onApply={onChange}
          onClose={() => {}}
          promptPlaceholder={aiHook.promptPlaceholder}
          promptDescription={aiHook.promptDescription}
          panelState={panelState}
          setPanelState={setPanelState}
        />
      </div>
    </AIGenerationProvider>
  );
}

export function EditorAIWrapperFF(props: Props) {
  const isIAEditorEnabled = useFeatureFlagEnabled('ai-rich-text-editor');
  if (!isIAEditorEnabled) {
    return props.children;
  }

  return <EditorAIWrapper {...props} />;
}
